import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Users, FileClock, FileCheck2, Building2, FolderKanban, AlertTriangle, ListX, LifeBuoy, Wallet, RefreshCw, DollarSign, Repeat, ChevronRight, CalendarClock, MessageSquareWarning } from "lucide-react";
import { Area, AreaChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useApi, useDirectory, useClientOptions } from "@/hooks/useApi";
import { DateInput } from "@/components/common/DateInput";
import { PageHeader, FilterBar, Loading, ErrorBox, EmptyState, Section } from "@/components/common/ui-bits";
import { optionList, PROJECT_STATUSES, PROJECT_TYPES, fmtDate, money } from "@/lib/format";

const CARDS = [
  ["leads_new", "Prospectos nuevos", Users, "blue"], ["proposals_pending", "Propuestas pendientes", FileClock, "amber"],
  ["proposals_accepted", "Propuestas aceptadas", FileCheck2, "emerald"], ["clients_active", "Clientes activos", Building2, "blue"],
  ["projects_in_progress", "Proyectos en proceso", FolderKanban, "violet"], ["projects_delayed", "Proyectos retrasados", AlertTriangle, "rose"],
  ["tasks_overdue", "Tareas vencidas", ListX, "rose"], ["requests_open", "Solicitudes abiertas", LifeBuoy, "amber"],
  ["payments_pending", "Pagos pendientes", Wallet, "orange"], ["renewals_upcoming", "Renovaciones próximas", RefreshCw, "teal"],
  ["changes_pending", "Cambios de clientes por resolver", MessageSquareWarning, "amber"],
  ["revenue_month", "Ingresos del mes", DollarSign, "emerald"], ["revenue_recurring", "Ingresos recurrentes", Repeat, "violet"],
];
const TONES = {
  blue: ["text-blue-600 bg-blue-50", "from-blue-500 to-blue-700"], amber: ["text-amber-600 bg-amber-50", "from-amber-400 to-orange-500"],
  emerald: ["text-emerald-600 bg-emerald-50", "from-emerald-400 to-emerald-600"], violet: ["text-violet-600 bg-violet-50", "from-violet-500 to-fuchsia-500"],
  rose: ["text-rose-600 bg-rose-50", "from-rose-500 to-red-600"], orange: ["text-orange-600 bg-orange-50", "from-orange-400 to-orange-600"],
  teal: ["text-teal-600 bg-teal-50", "from-teal-400 to-cyan-600"],
};
const PIE = ["#1D4ED8", "#F97316", "#10B981", "#8B5CF6", "#F43F5E", "#06B6D4", "#64748B", "#EAB308"];

function useCountUp(target) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (typeof target !== "number") { return undefined; }
    let raf; const start = performance.now();
    const step = (t) => { const p = Math.min(1, (t - start) / 700); setV(Math.round(target * (1 - (1 - p) ** 3))); if (p < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return typeof target === "number" ? v : target;
}

function KpiCard({ k, label, Icon, tone, card, onOpen }) {
  const restricted = card?.phase2;
  const value = useCountUp(card?.count);
  const [chip, bar] = TONES[tone];
  return (
    <button onClick={() => !restricted && onOpen()} disabled={restricted} data-testid={`kpi-card-${k}`}
      className="card-box card-lift group relative flex flex-col items-start gap-3 overflow-hidden p-5 text-left disabled:cursor-not-allowed disabled:opacity-70">
      <span className={`accent-bar bg-gradient-to-r ${bar}`} />
      <div className="flex w-full items-center justify-between">
        <span className={`chip ${chip}`}><Icon className="h-4 w-4" /></span>
        {!restricted && <ChevronRight className="h-4 w-4 text-slate-300 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-blue-600" />}
      </div>
      <p className="text-xs font-medium text-slate-500">{label}</p>
      {restricted ? <p className="text-xs font-semibold text-slate-400" data-testid={`kpi-value-${k}`}>Solo administrador</p>
        : <p className="num text-3xl font-semibold text-slate-900" data-testid={`kpi-value-${k}`}>{value ?? "—"}</p>}
    </button>
  );
}

function RevenueChart({ data }) {
  return (
    <Section title="Ingresos cobrados · últimos 6 meses" className="card-lift" testid="dash-chart-revenue">
      <div className="h-60"><ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ left: 4, right: 8, top: 8 }}>
          <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1D4ED8" stopOpacity={0.35} /><stop offset="100%" stopColor="#1D4ED8" stopOpacity={0} /></linearGradient></defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
          <XAxis dataKey="label" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} width={56} tickFormatter={(v) => `$${v}`} />
          <Tooltip formatter={(v) => money(v)} />
          <Area type="monotone" dataKey="amount" name="Cobrado" stroke="#1D4ED8" strokeWidth={2.5} fill="url(#rev)" animationDuration={900} />
        </AreaChart>
      </ResponsiveContainer></div>
    </Section>
  );
}

function Donut({ title, data, testid }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <Section title={title} className="card-lift" testid={testid}>
      {!total ? <EmptyState text="Sin datos." /> : <div className="flex items-center gap-4">
        <div className="relative h-44 w-44 shrink-0"><ResponsiveContainer width="100%" height="100%">
          <PieChart><Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={78} paddingAngle={3} animationDuration={900}>
            {data.map((d, i) => <Cell key={d.name} fill={PIE[i % PIE.length]} />)}</Pie><Tooltip /></PieChart>
        </ResponsiveContainer><span className="num absolute inset-0 flex items-center justify-center text-2xl font-semibold text-slate-900">{total}</span></div>
        <ul className="min-w-0 space-y-1.5 text-xs">{data.map((d, i) => <li key={d.name} className="flex items-center gap-2"><span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: PIE[i % PIE.length] }} /><span className="truncate text-slate-600">{d.name}</span><b className="ml-auto text-slate-900">{d.value}</b></li>)}</ul>
      </div>}
    </Section>
  );
}

function Timeline({ items }) {
  return (
    <Section title="Próximos vencimientos" className="card-lift" testid="dash-renewals-timeline">
      {!items.length ? <EmptyState text="Sin vencimientos en 90 días." /> : (
        <ol className="stagger relative space-y-4 border-l-2 border-dashed border-blue-100 pl-5">
          {items.slice(0, 6).map((it) => <li key={it.id} className="relative">
            <span className="absolute -left-[27px] top-1 flex h-4 w-4 items-center justify-center rounded-full bg-white ring-2 ring-orange-400"><CalendarClock className="h-2.5 w-2.5 text-orange-500" /></span>
            <Link to={it.link} className="block text-sm font-medium text-slate-800 hover:text-blue-700">{it.label}</Link>
            <p className="text-xs text-slate-500">{it.sub}</p></li>)}
        </ol>)}
    </Section>
  );
}

export default function Dashboard() {
  const [f, setF] = useState({});
  const { data, loading, error } = useApi("/dashboard", f);
  const { options: staff } = useDirectory();
  const clients = useClientOptions();
  const [open, setOpen] = useState(null);
  const current = open && data?.cards[open];
  const label = open && CARDS.find((c) => c[0] === open)[1];
  const ch = data?.charts;

  return (
    <div>
      <PageHeader title="Dashboard" subtitle={data ? `Hoy: ${fmtDate(data.today)} · America/Chicago` : ""} />
      <FilterBar testid="dashboard-filters" values={f} onChange={setF} filters={[
        { key: "client_id", label: "Cliente", options: clients }, { key: "responsible_id", label: "Responsable", options: staff },
        { key: "status", label: "Estado de proyecto", options: optionList(PROJECT_STATUSES) }, { key: "project_type", label: "Tipo de proyecto", options: optionList(PROJECT_TYPES) },
      ]}>
        <input className="field-input sm:w-44" type="text" placeholder="Servicio" value={f.service || ""} onChange={(e) => setF({ ...f, service: e.target.value })} data-testid="dashboard-filters-service" aria-label="Servicio" />
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-slate-500">Desde<DateInput className="w-40" placeholder="Fecha" value={f.date_from || ""} onChange={(v) => setF({ ...f, date_from: v })} testid="dashboard-filters-date-from" ariaLabel="Desde" /></label>
          <label className="flex items-center gap-1 text-xs text-slate-500">Hasta<DateInput className="w-40" placeholder="Fecha" value={f.date_to || ""} onChange={(v) => setF({ ...f, date_to: v })} testid="dashboard-filters-date-to" ariaLabel="Hasta" /></label>
        </div>
      </FilterBar>
      <ErrorBox text={error} />
      {loading && !data ? <Loading /> : <>
        <div className="stagger grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
          {CARDS.map(([k, l, Icon, tone]) => <KpiCard key={k} k={k} label={l} Icon={Icon} tone={tone} card={data?.cards[k]} onOpen={() => setOpen(k)} />)}
        </div>
        {ch && <div className="stagger mt-6 grid gap-6 lg:grid-cols-3">
          {ch.revenue ? <div className="lg:col-span-2"><RevenueChart data={ch.revenue} /></div> : <Donut title="Propuestas por estado" data={ch.proposals} testid="dash-chart-proposals" />}
          <Donut title="Proyectos por estado" data={ch.projects} testid="dash-chart-projects" />
          {ch.revenue && <Donut title="Propuestas por estado" data={ch.proposals} testid="dash-chart-proposals" />}
          <div className={ch.revenue ? "lg:col-span-2" : ""}><Timeline items={data.cards.renewals_upcoming?.items || []} /></div>
        </div>}
      </>}
      <Dialog open={Boolean(open)} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="glass max-h-[85vh] overflow-y-auto sm:max-w-xl" data-testid="kpi-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{label}</DialogTitle>
            <DialogDescription>{current?.items?.length ?? 0} registro(s){typeof current?.count === "string" ? ` · ${current.count}` : ""}</DialogDescription>
          </DialogHeader>
          {current?.items?.length ? (
            <ul className="stagger divide-y divide-slate-100">
              {current.items.map((it) => (
                <li key={it.id}><Link to={it.link} className="flex items-center justify-between gap-3 rounded-md px-2 py-3 transition-colors hover:bg-blue-50/70 hover:text-blue-700" data-testid={`kpi-item-${it.id}`}>
                  <span className="min-w-0"><span className="block truncate text-sm font-medium">{it.label}</span><span className="block truncate text-xs text-slate-500">{it.sub}</span></span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" /></Link></li>
              ))}
            </ul>
          ) : <EmptyState text="Nada por aquí." />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
